import _ from 'lodash';
import _differenceWith from 'lodash/differenceWith';
import _sortBy from 'lodash/sortBy';
import update from 'immutability-helper';
import React, { Component } from 'react';
import { withRouter } from 'react-router-dom';
import { gql } from '@apollo/client';
import { reverse } from 'named-urls';
import routes from '@achievable-me/tundra-wizard/routes';
import { evaluate, prepare } from '@achievable-me/tundra-wizard/evaluate';
import { buildChoices, getIsCorrect } from '@achievable-me/tundra-wizard/item';
import USER_EXAM_STATUS from '@achievable-me/tundra-wizard/userExamStatus';
import USER_EXAM_ITEM_STATUS from '@achievable-me/tundra-wizard/userExamItemStatus';
import { inflateContent } from '@achievable-me/tundra-wizard/contentHelpers';
import STEP from '$components/review/step';
import MODE from '$components/review/mode';
import PRODUCT_REVIEWS from '$queries/productReviews';
import { parsePrompt } from '$lib/quiz';
import { getSeed } from '$lib/seed';
import ReviewHeader from './ReviewHeader';
import ReviewSubheader from './ReviewSubheader';
import ReviewProgress from './ReviewProgress';
import ReviewContent from './ReviewContent';
import ReviewActions from './ReviewActions';
import ShareSeo from './ShareSeo';
import { updateInput } from './updateInput';
import styles from './styles.scss';

const EXCLUDE_CONTENT_UUIDS = [
  // FEATURE(multiproduct)
  'bc50b048-f4d2-4074-b690-e5174c47003e', // finra-sie intro
  'ab15190e-b6ee-4ea4-b115-59c082ad749e', // finra-sie outro
];

const REVIEW_SESSION_QUERY = gql`
  query ReviewSession($input: ReviewSessionInput!) {
    reviewSession(input: $input) {
      items {
        uuid
        code
        format
        itemType
        transpiled
        contentUuid
        hasTextbookContent
      }
    }
  }
`;

const USER_EXAM_QUERY = gql`
  query UserExam($input: UserExamInput!) {
    userExam(input: $input) {
      userExam {
        uuid
        score
        scoreSummary
        status
        startedAt
        finishedAt
        exam {
          uuid
          allottedTimeMins
          numQuestions
          product {
            uuid
            name
            slug
          }
        }
        userExamItems {
          choice
          code
          isFlagged
          itemEvaluated
          itemType
          itemUuid
          position
          seed
          score
          status
          uuid
        }
      }
    }
  }
`;

const ESSAY_RESPONSE_QUERY = gql`
  query EssayResponse($input: EssayResponseInput!) {
    essayResponse(input: $input) {
      essayResponse {
        uuid
        response
        score
        scoreMeta
        startedAt
        submitAt
        updatedAt
        essayPrompt {
          uuid
          type
          text
          prompt
          source
        }
      }
    }
  }
`;

const START_ESSAY_RESPONSE = gql`
  mutation StartEssayResponse($input: StartEssayResponseInput!) {
    startEssayResponse(input: $input) {
      essayResponse {
        uuid
        startedAt
      }
    }
  }
`;

const SAVE_ESSAY_RESPONSE = gql`
  mutation SaveEssayResponse($input: SaveEssayResponseInput!) {
    saveEssayResponse(input: $input) {
      essayResponse {
        uuid
        score
        scoreMeta
        submitAt
        updatedAt
      }
    }
  }
`;

const SUBMIT_ESSAY_RESPONSE = gql`
  mutation SubmitEssayResponse($input: SubmitEssayResponseInput!) {
    submitEssayResponse(input: $input) {
      essayResponse {
        uuid
        response
        score
        scoreMeta
        submitAt
      }
    }
  }
`;

const ANSWER_USER_EXAM_ITEM = gql`
  mutation AnswerUserExamItem($input: AnswerUserExamItemInput!) {
    answerUserExamItem(input: $input) {
      userExamItem {
        uuid
        choice
        status
        score
      }
    }
  }
`;

const MARK_USER_EXAM_ITEM = gql`
  mutation MarkUserExamItem($input: MarkUserExamItemInput!) {
    markUserExamItem(input: $input) {
      userExamItem {
        uuid
        isFlagged
      }
    }
  }
`;

const FINISH_USER_EXAM = gql`
  mutation FinishUserExam($input: FinishUserExamInput!) {
    finishUserExam(input: $input) {
      userExam {
        uuid
        score
        scoreSummary
        status
        startedAt
        finishedAt
        exam {
          uuid
          allottedTimeMins
          numQuestions
          product {
            uuid
            name
            slug
          }
        }
      }
    }
  }
`;

const QUIZ_QUERY = gql`
  query QuizSession($input: QuizSessionInput!) {
    quizSession(input: $input) {
      items {
        uuid
        code
        itemType
        transpiled
        contentUuid
        hasTextbookContent
      }
      product {
        uuid
        slug
        name
        shortName
        content {
          uuid
          slug
          parentUuid
          position
          name
        }
      }
    }
  }
`;

const STAFF_ITEM_QUERY = gql`
  query StaffItem($input: StaffItemInput!) {
    staffItem(input: $input) {
      item {
        uuid
        code
        itemType
        transpiled
        contentUuid
        hasTextbookContent
        extra
      }
    }
  }
`;

const INITIAL_REVIEW_STATE = {
  quizQueue: null,
  step: STEP.loading,
  summary: null,
  sessionHistory: [],
};

const INITIAL_EXAM_STATE = {
  examQueueIndex: -1,
  quizQueue: null,
  step: STEP.loading,
  userExam: null,
};

const INITIAL_DEBUG_STATE = INITIAL_REVIEW_STATE;
const INITIAL_SHARE_STATE = INITIAL_REVIEW_STATE;

const INITIAL_ESSAY_STATE = {
  essayPrompt: null,
  essayResponse: null,
  step: STEP.loading,
};

const INITIAL_OPTIONS = {
  fill: false,
  shuffle: false,
  size: 10,
  contentUuids: null,
};

const buildInput = ({ rawInput }) => {
  if (!rawInput || _.isEmpty(rawInput)) {
    return {};
  }

  try {
    return JSON.parse(rawInput);
  } catch (e) {
    // 20200505JP: If we can't parse as JSON, we're dealing with v0 choices.
    return {
      v: 0,
      s: rawInput,
    };
  }
};

const prepareQuizQueue = ({ items, fill, size, shuffle }) => {
  if (fill && items.length < size) {
    const neededItems = size - items.length;
    const randomizedItems = items.filter((item) => item.format === 'raw');

    for (let i = 0; i < neededItems; i += 1) {
      const randomizedItem =
        randomizedItems[(i + randomizedItems.length) % randomizedItems.length];
      const clonedItem = _.cloneDeep(randomizedItem);
      items.push(clonedItem);
    }
  }

  const queue = items.map((item) => {
    const { choice: rawInput, itemEvaluated, transpiled, seed: itemSeed } = item;

    const seed = itemSeed || getSeed();

    const evaluated = (() => {
      if (!itemEvaluated) {
        return prepare(evaluate(transpiled, { seed }));
      }

      const parsed = JSON.parse(itemEvaluated);

      // 20200621JP: This is a v0 exam quiz that needs to be prepared.
      if (!parsed.choices) {
        return prepare(parsed, { seed });
      }

      // 20200621JP: v1 exam quizzes have already been prepared on the server.
      return parsed;
    })();

    const { explanation, prompt } = evaluated;
    const input = buildInput({ rawInput });
    const choices = buildChoices({ evaluated });

    return {
      quiz: {
        seed,
        prompt,
        explanation,
        choices,
        evaluated,
        input,
      },
      item,
    };
  });

  const finalQueue = shuffle ? _.shuffle(queue) : queue;

  return finalQueue;
};

const updateQuizQueueWithStatusAndScore = ({ status, score, quizQueue, index }) => {
  return update(quizQueue, {
    [index]: {
      item: {
        score: {
          $set: score,
        },
        status: {
          $set: status,
        },
      },
    },
  });
};

const updateQuizQueueWithSelectedChoice = ({ choice, quizQueue, index }) => {
  const { quiz } = quizQueue[index];
  const { input: prevInput, evaluated } = quiz;
  const itemType = evaluated.itemType || 'multipleChoice';

  const input = updateInput({ choice, prevInput, evaluated, itemType });

  const isCorrect = getIsCorrect({ input, evaluated, itemType });
  const score = isCorrect ? 1 : 0;
  const status = isCorrect
    ? USER_EXAM_ITEM_STATUS.correct
    : USER_EXAM_ITEM_STATUS.incorrect;

  const newQueue = update(quizQueue, {
    [index]: {
      quiz: {
        input: {
          $set: input,
        },
      },
      item: {
        score: {
          $set: score,
        },
        status: {
          $set: status,
        },
      },
    },
  });

  return newQueue;
};

const updateQuizQueueWithMark = ({ isFlagged, quizQueue, index }) => {
  return update(quizQueue, {
    [index]: {
      item: {
        isFlagged: {
          $set: isFlagged,
        },
      },
    },
  });
};

class ReviewMain extends Component {
  constructor(props) {
    super(props);

    const { mode } = props;

    const INITIAL_STATES = {
      [MODE.review]: INITIAL_REVIEW_STATE,
      [MODE.exam]: INITIAL_EXAM_STATE,
      [MODE.debug]: INITIAL_DEBUG_STATE,
      [MODE.share]: INITIAL_SHARE_STATE,
      [MODE.essay]: INITIAL_ESSAY_STATE,
    };
    const initialState = INITIAL_STATES[mode];

    this.localEssayResponse = null;

    this.state = {
      ...initialState,
      showPassage: false,
      options: INITIAL_OPTIONS,
    };
  }

  componentDidMount = async () => {
    const { mode } = this.props;

    const LOADERS = {
      [MODE.review]: this.loadModeReview,
      [MODE.exam]: this.loadModeExam,
      [MODE.debug]: this.loadModeDebug,
      [MODE.share]: this.loadModeShare,
      [MODE.essay]: this.loadModeEssay,
    };

    const loader = LOADERS[mode];
    loader();
  };

  loadModeReview = async () => {
    const { client, productSlug } = this.props;
    const { options } = this.state;
    const { data } = await client.query({
      query: PRODUCT_REVIEWS,
      variables: {
        input: {
          productSlug,
        },
      },
      fetchPolicy: 'network-only',
    });
    const { productReviews } = data;
    const {
      product,
      content,
      itemsReady,
      itemsAvailable,
      greQuantItemsReady,
      greVocabItemsReady,
    } = productReviews;

    const allContentUuids = content.map((c) => c.uuid);

    const { inflatedContent } = inflateContent({ product, content });
    const contentSkeleton = _differenceWith(
      inflatedContent,
      EXCLUDE_CONTENT_UUIDS,
      (arrVal, othVal) => {
        return arrVal.uuid === othVal;
      }
    );

    const nextOptions =
      options.contentUuids !== null
        ? options
        : update(options, { contentUuids: { $set: allContentUuids } });

    this.setState({
      allContentUuids,
      step: STEP.sessionSelect,
      product,
      content,
      contentSkeleton,
      itemsAvailable,
      itemsReady,
      greQuantItemsReady,
      greVocabItemsReady,
      options: nextOptions,
    });
  };

  loadModeExam = async () => {
    const { client, presentationManager, userExamUuid } = this.props;

    const { data } = await client.query({
      query: USER_EXAM_QUERY,
      variables: {
        input: {
          uuid: userExamUuid,
        },
      },
      fetchPolicy: 'network-only',
    });

    const {
      userExam: { userExam },
    } = data;
    const { finishedAt, userExamItems } = userExam;

    if (finishedAt) {
      presentationManager.stop();
    }

    const sortedUserExamItems = _sortBy(userExamItems, 'position');
    const {
      exam: { product },
    } = userExam;

    const quizQueue = prepareQuizQueue({ items: sortedUserExamItems });

    this.setState({
      quizQueue,
      userExam,
      product,
      step: STEP.examSummary,
    });
  };

  loadModeEssay = async () => {
    const { client, essayResponseUuid } = this.props;

    const { data } = await client.query({
      query: ESSAY_RESPONSE_QUERY,
      variables: {
        input: {
          essayResponseUuid,
        },
      },
      fetchPolicy: 'network-only',
    });

    const {
      essayResponse: { essayResponse },
    } = data;
    const { essayPrompt } = essayResponse;

    this.localEssayResponse = essayResponse.response;

    const step = (() => {
      if (essayResponse.submitAt) {
        return STEP.essayReview;
      }

      if (essayResponse.startedAt) {
        return STEP.essayWriting;
      }

      return STEP.essayInstructions;
    })();

    this.setState({
      essayResponse,
      essayPrompt,
      step,
    });
  };

  loadModeDebug = async () => {
    const { client, debug } = this.props;

    const { data } = await client.query({
      query: STAFF_ITEM_QUERY,
      variables: {
        input: {
          code: debug,
        },
      },
      fetchPolicy: 'network-only',
    });

    const {
      staffItem: { item },
    } = data;

    const quizQueue = prepareQuizQueue({ items: [item] });

    this.setState({
      quizQueue,
      step: STEP.quizPrompt,
      summary: {
        answered: 0,
        total: quizQueue.length,
        correct: 0,
        incorrect: 0,
      },
    });
  };

  loadModeShare = async () => {
    const { client, match, presentationManager } = this.props;
    const {
      params: { quizCode: code, productSlug },
    } = match;

    const { data } = await client.query({
      query: QUIZ_QUERY,
      variables: {
        input: {
          code,
          productSlug,
        },
      },
    });

    const {
      quizSession: { items, product },
    } = data;

    const quizQueue = prepareQuizQueue({ items });
    presentationManager.track({ entry: quizQueue[0] });

    const quizObj = quizQueue[0];
    const {
      quiz: { prompt },
    } = quizObj;

    const { truncatedTitle: title } = parsePrompt({ code, prompt });

    this.setState({
      quizQueue,
      step: STEP.quizPrompt,
      product,
      title,
      summary: {
        answered: 0,
        total: quizQueue.length,
        correct: 0,
        incorrect: 0,
      },
    });
  };

  handleToggleShowPassage = () => {
    this.setState({
      showPassage: !this.state.showPassage,
    });
  };

  handleExit = () => {
    const { history, mode, productSlug } = this.props;

    if (mode === MODE.exam || mode === MODE.essay) {
      history.push(reverse(routes.study.exams, { productSlug }));
      return;
    }

    history.push(reverse(routes.study.dashboard, { productSlug }));
  };

  handleConfirm = () => {
    const { presentationManager } = this.props;
    const { quizQueue, summary, sessionHistory } = this.state;

    const index = 0;
    const quizObj = quizQueue[index];
    const { quiz } = quizObj;
    const { evaluated, input } = quiz;
    const itemType = evaluated.itemType || 'multipleChoice';

    const isCorrect = getIsCorrect({ input, evaluated, itemType });

    const nextSessionHistory = update(sessionHistory, {
      $push: [
        {
          quizObj,
          isCorrect,
        },
      ],
    });

    const updatedSummary = isCorrect
      ? update(summary, {
          correct: { $set: summary.correct + 1 },
        })
      : update(summary, {
          incorrect: { $set: summary.incorrect + 1 },
        });

    this.setState({
      step: STEP.quizConfirmation,
      summary: updatedSummary,
      sessionHistory: nextSessionHistory,
    });

    presentationManager.track({ stage: 'review', isCorrect });
  };

  handleContinue = () => {
    const { presentationManager } = this.props;
    const { quizQueue, summary } = this.state;

    const updatedSummary = update(summary, {
      answered: { $set: summary.answered + 1 },
    });

    this.setState({
      summary: updatedSummary,
    });

    presentationManager.record();

    if (quizQueue.length === 1) {
      this.setState({
        step: STEP.summary,
        quizQueue: null,
      });
      return;
    }

    this.setState({
      step: STEP.quizPrompt,
      quizQueue: update(quizQueue, { $splice: [[0, 1]] }),
    });

    presentationManager.track({ entry: quizQueue[1] });
  };

  handleFinish = () => {
    this.setState({
      step: STEP.summary,
      quizQueue: null,
    });
  };

  handleMarkExamItem = async ({ isFlagged }) => {
    const { client } = this.props;
    const { examQueueIndex, quizQueue, userExam } = this.state;

    if (userExam.status !== USER_EXAM_STATUS.started) {
      return;
    }

    const updatedQuizQueue = updateQuizQueueWithMark({
      quizQueue,
      isFlagged,
      index: examQueueIndex,
    });

    this.setState({
      quizQueue: updatedQuizQueue,
    });

    const quizObj = quizQueue[examQueueIndex];
    const { item } = quizObj;

    await client.mutate({
      mutation: MARK_USER_EXAM_ITEM,
      variables: {
        input: {
          userExamItemUuid: item.uuid,
          isFlagged,
        },
      },
    });
  };

  handleChoiceExam = async ({ choice }) => {
    const { client, presentationManager } = this.props;
    const { examQueueIndex, quizQueue, userExam } = this.state;

    if (userExam.status !== USER_EXAM_STATUS.started) {
      return;
    }

    let updatedQuizQueue = updateQuizQueueWithSelectedChoice({
      quizQueue,
      choice,
      index: examQueueIndex,
    });

    this.setState({
      quizQueue: updatedQuizQueue,
    });

    const quizObj = updatedQuizQueue[examQueueIndex];
    const { item, quiz } = quizObj;
    const { input, evaluated } = quiz;

    const itemType = evaluated.itemType || 'multipleChoice';
    const isCorrect = getIsCorrect({ input, evaluated, itemType });
    presentationManager.track({ stage: 'answered', isCorrect });

    const { data } = await client.mutate({
      mutation: ANSWER_USER_EXAM_ITEM,
      variables: {
        input: {
          userExamItemUuid: item.uuid,
          choice: JSON.stringify(input),
        },
      },
    });

    const {
      answerUserExamItem: { userExamItem },
    } = data;

    updatedQuizQueue = updateQuizQueueWithStatusAndScore({
      quizQueue: this.state.quizQueue,
      index: examQueueIndex,
      score: userExamItem.score,
      status: userExamItem.status,
    });

    this.setState({
      quizQueue: updatedQuizQueue,
    });
  };

  handleChoice = ({ choice }) => {
    const { quizQueue, step } = this.state;

    if (step !== STEP.quizPrompt) {
      return;
    }

    const updatedQuizQueue = updateQuizQueueWithSelectedChoice({
      quizQueue,
      choice,
      index: 0,
    });
    this.setState({
      quizQueue: updatedQuizQueue,
    });
  };

  handleInput = async ({ input }) => {
    const { mode } = this.props;
    const { examQueueIndex, quizQueue, step, userExam } = this.state;

    if (step !== STEP.quizPrompt) {
      return;
    }

    if (mode === MODE.exam) {
      if (userExam.status !== USER_EXAM_STATUS.started) {
        return;
      }
    }

    const index = mode === MODE.exam ? examQueueIndex : 0;

    const updatedQuizQueue = updateQuizQueueWithSelectedChoice({
      quizQueue,
      choice: input,
      index,
    });
    this.setState({
      quizQueue: updatedQuizQueue,
    });

    if (mode === MODE.exam) {
      const { client } = this.props;

      const quizObj = updatedQuizQueue[examQueueIndex];
      const { item, quiz } = quizObj;
      const { input: normalizedInput } = quiz;

      // 20200521JP: No need to await.
      client.mutate({
        mutation: ANSWER_USER_EXAM_ITEM,
        variables: {
          input: {
            userExamItemUuid: item.uuid,
            choice: JSON.stringify(normalizedInput),
          },
        },
      });
    }
  };

  handleStart = async () => {
    const { client, presentationManager, productSlug } = this.props;
    const { options } = this.state;
    const { contentUuids, fill, size, shuffle } = options;

    this.setState({
      step: STEP.loading,
    });

    const { data } = await client.query({
      query: REVIEW_SESSION_QUERY,
      variables: {
        input: {
          size,
          contentUuids,
          productSlug,
          shuffle,
        },
      },
      fetchPolicy: 'network-only',
    });

    const { reviewSession } = data;
    const { items } = reviewSession;

    const quizQueue = prepareQuizQueue({ items, fill, size, shuffle });

    presentationManager.track({ entry: quizQueue[0] });

    this.setState({
      quizQueue,
      step: STEP.quizPrompt,
      summary: {
        answered: 0,
        total: quizQueue.length,
        correct: 0,
        incorrect: 0,
      },
    });
  };

  handleDebugReset = () => {
    this.setState(INITIAL_DEBUG_STATE, () => {
      this.loadModeDebug();
    });
  };

  handleMore = () => {
    const { presentationManager } = this.props;
    presentationManager.resetSession();
    this.setState(INITIAL_REVIEW_STATE, () => {
      this.loadModeReview();
    });
  };

  handleFinishExam = async () => {
    const { client, presentationManager } = this.props;
    const { userExam } = this.state;

    presentationManager.stop();

    this.setState({
      step: STEP.loading,
    });

    const { data } = await client.mutate({
      mutation: FINISH_USER_EXAM,
      variables: {
        input: {
          userExamUuid: userExam.uuid,
        },
      },
    });

    const {
      finishUserExam: { userExam: updatedUserExam },
    } = data;

    this.setState({
      userExam: updatedUserExam,
      step: STEP.examSummary,
      examQueueIndex: -1,
    });
  };

  handleExamNext = () => {
    const { presentationManager } = this.props;
    const { examQueueIndex, quizQueue, step } = this.state;
    const nextQueueIndex = examQueueIndex + 1;

    if (step === STEP.quizPrompt) {
      presentationManager.record();
    }
    presentationManager.track({ entry: quizQueue[nextQueueIndex] });

    this.handleQueueNavigation({ queueIndex: nextQueueIndex });
  };

  handleExamPrev = () => {
    const { presentationManager } = this.props;
    const { examQueueIndex, quizQueue, step } = this.state;

    if (step === STEP.quizPrompt) {
      presentationManager.record();
    }

    if (step === STEP.examReview) {
      const nextQueueIndex = quizQueue.length - 1;
      presentationManager.track({ entry: quizQueue[nextQueueIndex] });
      this.handleQueueNavigation({ queueIndex: quizQueue.length - 1 });
      return;
    }

    const nextQueueIndex = examQueueIndex - 1;
    presentationManager.track({ entry: quizQueue[nextQueueIndex] });
    this.handleQueueNavigation({ queueIndex: nextQueueIndex });
  };

  handleExamReview = () => {
    const { presentationManager } = this.props;
    const { step } = this.state;
    if (step === STEP.quizPrompt) {
      presentationManager.record();
    }
    this.setState({
      step: STEP.examReview,
    });
  };

  handleExamSummary = () => {
    this.setState({
      step: STEP.examSummary,
      examQueueIndex: -1,
    });
  };

  handleOpenExamNavigation = () => {
    const { step } = this.state;
    if (step === STEP.quizPrompt) {
      const { presentationManager } = this.props;
      presentationManager.record();
    }
    this.setState({
      step: STEP.examNavigation,
    });
  };

  handleShowOptions = () => {
    this.setState({
      step: STEP.sessionOptions,
    });
  };

  handleOptionsReset = () => {
    const { allContentUuids, options } = this.state;
    const { size } = options;
    this.setState({
      step: STEP.sessionSelect,
      options: {
        ...INITIAL_OPTIONS,
        size,
        contentUuids: allContentUuids,
      },
    });
  };

  handleOptionsApply = () => {
    this.setState({
      step: STEP.sessionSelect,
    });
  };

  handleShowQuizConfirmation = () => {
    this.setState({
      step: STEP.quizConfirmation,
    });
  };

  handleShowQuizExplanation = () => {
    this.setState({
      step: STEP.quizExplanation,
    });
  };

  handleQueueNavigation = ({ queueIndex }) => {
    const { mode, presentationManager } = this.props;
    const { quizQueue, step: prevStep, userExam } = this.state;
    const isFinished = userExam.status === USER_EXAM_STATUS.finished;
    const step = isFinished ? STEP.quizConfirmation : STEP.quizPrompt;

    if (mode === MODE.exam && prevStep === STEP.examNavigation) {
      presentationManager.track({ entry: quizQueue[queueIndex] });
    }

    this.setState({
      step,
      examQueueIndex: queueIndex,
    });
  };

  handleEssayOnChange = ({ response }) => {
    this.localEssayResponse = response;
  };

  handleEssaySave = async () => {
    const { client } = this.props;
    const { essayResponse } = this.state;
    const { uuid: essayResponseUuid } = essayResponse;

    if (!this.localEssayResponse || _.isEmpty(this.localEssayResponse)) {
      return;
    }

    // 20200622JP: Ignore save requests if essay has already been submit.
    if (this.essaySubmitAt || essayResponse.submitAt) {
      return;
    }

    // 20200622JP: Race conditions could be an issue if short save intervals.
    const { data } = await client.mutate({
      mutation: SAVE_ESSAY_RESPONSE,
      variables: {
        input: {
          essayResponseUuid,
          response: this.localEssayResponse,
        },
      },
    });

    const {
      saveEssayResponse: { essayResponse: receivedEssayResponse },
    } = data;

    this.setState((currentState) => {
      // 20200621JP: Without clone it doesn't assign for some reason...
      const updatedEssayResponse = _.assign(_.clone(currentState.essayResponse), {
        updatedAt: receivedEssayResponse.updatedAt,
      });
      return {
        essayResponse: updatedEssayResponse,
      };
    });
  };

  handleEssayStart = async () => {
    const { client } = this.props;
    const { essayResponse } = this.state;
    const { uuid: essayResponseUuid } = essayResponse;

    this.setState({
      step: STEP.essayWriting,
    });

    const { data } = await client.mutate({
      mutation: START_ESSAY_RESPONSE,
      variables: {
        input: {
          essayResponseUuid,
        },
      },
    });

    const {
      startEssayResponse: { essayResponse: receivedEssayResponse },
    } = data;

    this.localEssayResponse = receivedEssayResponse.response;

    // 20200621JP: Kick off the timer
    this.setState((currentState) => {
      // 20200621JP: Without clone it doesn't assign for some reason...
      const updatedEssayResponse = _.assign(_.clone(currentState.essayResponse), {
        startedAt: receivedEssayResponse.startedAt,
      });
      return {
        essayResponse: updatedEssayResponse,
      };
    });
  };

  handleEssaySubmit = async () => {
    const { client } = this.props;
    const { essayResponse } = this.state;
    const { uuid: essayResponseUuid } = essayResponse;

    const { data } = await client.mutate({
      mutation: SUBMIT_ESSAY_RESPONSE,
      variables: {
        input: {
          essayResponseUuid,
          response: this.localEssayResponse,
        },
      },
    });

    const {
      submitEssayResponse: { essayResponse: receivedEssayResponse },
    } = data;

    this.setState((currentState) => {
      // 20200621JP: Without clone it doesn't assign for some reason...
      const updatedEssayResponse = _.assign(_.clone(currentState.essayResponse), {
        score: receivedEssayResponse.score,
        scoreMeta: receivedEssayResponse.scoreMeta,
        response: receivedEssayResponse.response,
        submitAt: receivedEssayResponse.submitAt,
      });

      return {
        step: STEP.essayReview,
        essayResponse: updatedEssayResponse,
      };
    });
  };

  handleEssayViewEssay = () => {
    this.setState({
      step: STEP.essayWriting,
    });
  };

  handleEssayViewResults = () => {
    this.setState({
      step: STEP.essayReview,
    });
  };

  setOptions = (options) => {
    this.setState({
      options,
    });
  };

  render() {
    const { mode, productSlug } = this.props;
    const { state } = this;
    const { title } = state;

    const handleChoice = mode === MODE.exam ? this.handleChoiceExam : this.handleChoice;

    return (
      <div className={styles.layout}>
        {mode === MODE.share && <ShareSeo title={title} />}
        <ReviewHeader
          handleExit={this.handleExit}
          handleFinish={this.handleFinish}
          handleOpenExamNavigation={this.handleOpenExamNavigation}
          mode={mode}
          quizState={state}
        />
        <div className={styles.frame}>
          <div className={styles.content}>
            <ReviewSubheader
              mode={mode}
              quizState={state}
              handleShowOptions={this.handleShowOptions}
            />
            <ReviewProgress mode={mode} quizState={state} />
            <ReviewContent
              handleChoice={handleChoice}
              handleEssayOnChange={this.handleEssayOnChange}
              handleEssaySave={this.handleEssaySave}
              handleFinishExam={this.handleFinishExam}
              handleInput={this.handleInput}
              handleQueueNavigation={this.handleQueueNavigation}
              handleToggleShowPassage={this.handleToggleShowPassage}
              mode={mode}
              productSlug={productSlug}
              quizState={state}
              setOptions={this.setOptions}
            />
          </div>
          <ReviewActions
            handleConfirm={this.handleConfirm}
            handleContinue={this.handleContinue}
            handleDebugReset={this.handleDebugReset}
            handleEssayStart={this.handleEssayStart}
            handleEssaySubmit={this.handleEssaySubmit}
            handleEssayViewEssay={this.handleEssayViewEssay}
            handleEssayViewResults={this.handleEssayViewResults}
            handleExamNext={this.handleExamNext}
            handleExamPrev={this.handleExamPrev}
            handleExamReview={this.handleExamReview}
            handleExamSummary={this.handleExamSummary}
            handleExit={this.handleExit}
            handleMarkExamItem={this.handleMarkExamItem}
            handleMore={this.handleMore}
            handleOptionsApply={this.handleOptionsApply}
            handleOptionsReset={this.handleOptionsReset}
            handleShowQuizConfirmation={this.handleShowQuizConfirmation}
            handleShowQuizExplanation={this.handleShowQuizExplanation}
            handleStart={this.handleStart}
            handleToggleShowPassage={this.handleToggleShowPassage}
            mode={mode}
            quizState={state}
          />
        </div>
      </div>
    );
  }
}

export default withRouter(ReviewMain);
